/**
 * Partial Repayment Calculator Page
 * Standalone page for partial repayment calculations
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import {
  PartialRepaymentForm,
  PartialRepaymentResults,
  PartialRepaymentSchedule,
  type PartialRepaymentData,
  type PartialRepaymentResultsData,
} from '../../components/features/calculator';
import Toast from '../../components/common/Toast';
import { calculatorApi } from '../../services/api';
import { trackPageView, trackError } from '../../utils/analytics';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';
import SEO from '../../components/common/SEO';

export default function PartialRepaymentPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [results, setResults] = useState<PartialRepaymentResultsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'warning' | 'success' | 'info' } | null>(null);

  useEffect(() => {
    trackPageView('/calculators/partial-repayment', 'Partial Repayment Calculator');
    document.title = `${t('calculators.partialRepayment.title')} | PayWise365`;
  }, [t]);

  const seoTitle = isEnglish
    ? 'Mortgage Partial Repayment Calculator Hong Kong 2026 - Save Interest | PayWise365'
    : '按揭部分還款計算機香港 2026 - 節省利息 | PayWise365';

  const seoDescription = isEnglish
    ? 'Calculate how much you can save with partial mortgage repayments. Compare reducing payment vs reducing term strategies. See exact interest savings and new payment schedules for Hong Kong mortgages.'
    : '計算部分還款可節省多少。比較減供款與減年期策略。查看確切的利息節省和香港按揭新還款時間表。';

  const seoKeywords = isEnglish
    ? 'partial repayment calculator, early mortgage payment HK, 部分還款, save interest mortgage, partial payment Hong Kong, mortgage calculator'
    : '部分還款計算機, 提早還款, 節省利息, 按揭策略, 香港按揭';

  const handleCalculate = async (request: PartialRepaymentData) => {
    setLoading(true);
    setToast(null);
    setShowSchedule(false);

    try {
      const calculationResults = await calculatorApi.calculatePartialRepayment(request);
      setResults(calculationResults);
    } catch (err: any) {
      const rawError = err.response?.data?.detail || '';

      let translatedMessage: string;
      let toastType: 'error' | 'warning' = 'error';

      if (rawError.includes('VALIDATION_ERROR:')) {
        const errorKey = rawError.replace('VALIDATION_ERROR:', '').trim();
        translatedMessage = t(`errors.${errorKey}`, t('errors.calculation'));
        toastType = 'warning';
      } else {
        translatedMessage = t('errors.calculation');
      }

      setToast({
        message: translatedMessage,
        type: toastType
      });

      trackError('partial_repayment_calculation_error', rawError, 'api_call');
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = () => {
    setShowSchedule(true);
  };

  return (
    <div className="calculator-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/calculators/partial-repayment"
        ogType="website"
      />
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.partialRepayment.title')}</h1>
          <p>{t('calculators.partialRepayment.pageSubtitle')}</p>
        </div>
      </div>

      <main className="calculator-content">
        <div className="calculator-container">
          <PartialRepaymentForm
            onCalculate={handleCalculate}
            isLoading={loading}
          />
          {results && (
            <PartialRepaymentResults
              results={results}
              onViewSchedule={handleViewSchedule}
            />
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showSchedule && results?.payment_schedule && (
        <PartialRepaymentSchedule
          schedule={results.payment_schedule}
          onClose={() => setShowSchedule(false)}
          newTotalPayment={results.new_total_remaining_payment}
          newTotalInterest={results.new_total_interest}
          newRemainingInstalments={results.new_remaining_instalments}
        />
      )}
    </div>
  );
}
